import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { pageMeta } from "@/lib/seo";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-shim";
import { useEffect, useRef, useState } from "react";
import { getMyTrip } from "@/lib/trip.functions";
import { getMe } from "@/lib/profile.functions";
import { savePermissions, getPermissions } from "@/lib/media.functions";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  MapPin,
  Camera,
  Bell,
  LogOut,
  Trash2,
  Fingerprint,
  ChevronRight,
  Settings as SettingsIcon,
  Upload,
  Shield,
  Compass,
  Layers,
  Radio,
  CloudOff,
  Settings2,
  ChevronDown,
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { resetOnboarding, requestOpenOnboarding } from "@/lib/onboarding/state";
import { startTour } from "@/lib/onboarding/tour-state";
import { openAppSettings } from "@/lib/native/open-app-settings";
import { SyncOutboxCard } from "@/components/SyncOutboxCard";
import { GpsHealthPanel } from "@/components/GpsHealthPanel";

import {
  RoavaPage,
  RoavaCard,
  StatusChip,
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  type StatusChipVariant,
} from "@/components/roava";

import { GpsRateSheet } from "@/components/permissions/GpsRateSheet";
import { PodsSheet } from "@/components/permissions/PodsSheet";
import { getPodPreset, getPodCustomRadiusM, getPodCustomGapMin, POD_PREFS_CHANGED_EVENT, POD_PRESETS, getLiveActivityEnabled, setLiveActivityEnabled } from "@/lib/prefs";
import { liveActivityController } from "@/lib/live-activity/controller";

import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  requestLocationAlways,
  requestPhotoLibraryReadWrite,
  requestNotificationsPermission,
  getLocationAuthLevel,
  isNative,
  isBiometricUnlockEnabled,
  setBiometricUnlockEnabled,
  verifyBiometric,
  biometryTypeLabel,
} from "@/lib/native";
import { PhotoScan } from "@/lib/native/photo-scan";
import { BuildInfoLine } from "@/components/BuildInfoLine";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/_authenticated/permissions")({
  head: () =>
    pageMeta({
      title: "Settings — Roava by WGP Apps",
      description: "Manage your trip setup, privacy, uploads, and account.",
      path: "/permissions",
    }),
  component: Permissions,
});

type LocationState = "not_requested" | "enabled" | "limited" | "blocked" | "disabled";
type PermStatus = "not_granted" | "waiting" | "enabled" | "limited" | "blocked";

function statusToChip(
  status: PermStatus,
  labels: { enabled: string; not_granted: string; limited: string; checking: string; blocked: string },
): { variant: StatusChipVariant; label: string } {
  switch (status) {
    case "enabled":
      return { variant: "success", label: labels.enabled };
    case "limited":
      return { variant: "warning", label: labels.limited };
    case "waiting":
      return { variant: "warning", label: labels.checking };
    case "blocked":
      return { variant: "danger", label: labels.blocked };
    case "not_granted":
    default:
      return { variant: "neutral", label: labels.not_granted };
  }
}


// ---------- Reusable card patterns (UI-only, local to this file) ----------

function SectionHeader({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="mt-6 mb-3">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--roava-text-muted)]">
        {title}
      </p>
      {hint && (
        <p className="mt-1 text-xs text-[var(--roava-text-muted)]">{hint}</p>
      )}
    </div>
  );
}

function NavCard({
  icon,
  iconTone = "blue",
  title,
  description,
  value,
  onClick,
}: {
  icon: React.ReactNode;
  iconTone?: "blue" | "green" | "orange" | "red" | "gray";
  title: React.ReactNode;
  description?: React.ReactNode;
  value?: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <RoavaCard
      onClick={onClick}
      className="cursor-pointer hover:bg-[var(--roava-gray)]/40 transition"
      icon={icon}
      iconTone={iconTone}
      title={
        <div className="flex items-center justify-between gap-2 min-w-0">
          <span className="min-w-0 flex-1 break-words">{title}</span>
          <div className="flex items-center gap-1.5 shrink-0 whitespace-nowrap">
            {value}
            <ChevronRight className="size-4 text-[var(--roava-text-muted)]" />
          </div>
        </div>
      }
      description={description}
    />
  );
}

// ---------- Main ----------

function Permissions() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const tripFn = useServerFn(getMyTrip);
  const permsFn = useServerFn(getPermissions);
  const saveFn = useServerFn(savePermissions);
  const meFn = useServerFn(getMe);

  const { data: tripData, isFetching: isFetchingTrip } = useQuery({
    queryKey: ["my-trip"],
    queryFn: () => tripFn({}),
    refetchOnMount: "always",
    staleTime: 0,
  });
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: () => meFn({}) });
  const tripId = tripData?.trip?.id;

  const { data: permsData } = useQuery({
    queryKey: ["perms", tripId],
    queryFn: () => permsFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
  });

  const [locState, setLocStateRaw] = useState<LocationState>("not_requested");
  const [photos, setPhotosRaw] = useState(false);
  const [photoLimited, setPhotoLimited] = useState(false);
  const [wifi, setWifiRaw] = useState(true);
  const [notif, setNotifRaw] = useState(false);
  const [notifBlocked, setNotifBlocked] = useState(false);

  const [gpsSheet, setGpsSheet] = useState(false);
  const [podsSheet, setPodsSheet] = useState(false);
  const [podRev, setPodRev] = useState(0);
  useEffect(() => {
    const h = () => setPodRev((n) => n + 1);
    window.addEventListener(POD_PREFS_CHANGED_EVENT, h);
    return () => window.removeEventListener(POD_PREFS_CHANGED_EVENT, h);
  }, []);
  // Walkthrough hooks: let the tour open/close the GPS sheet programmatically.
  useEffect(() => {
    const open = () => setGpsSheet(true);
    const close = () => setGpsSheet(false);
    window.addEventListener("roava:tour:open-gps-sheet", open);
    window.addEventListener("roava:tour:close-gps-sheet", close);
    return () => {
      window.removeEventListener("roava:tour:open-gps-sheet", open);
      window.removeEventListener("roava:tour:close-gps-sheet", close);
    };
  }, []);
  const podChipLabel = (() => {
    void podRev;
    const p = getPodPreset();
    if (p === "tight") return t("perms.pod_tight");
    if (p === "loose") return t("perms.pod_loose");
    if (p === "custom") {
      const r = getPodCustomRadiusM();
      const g = getPodCustomGapMin();
      const rStr = r >= 1000 ? `${(r / 1000).toFixed(r >= 10000 ? 0 : 1)} km` : `${r} m`;
      const gStr = g < 60 ? `${g} ${t("perms.unit_min")}` : `${(g / 60).toFixed(g % 60 === 0 ? 0 : 1)} ${t("perms.unit_h")}`;
      return t("perms.pod_custom", { radius: rStr, gap: gStr });
    }
    return t("perms.pod_standard");
  })();
  void POD_PRESETS;
  const [locWaiting, setLocWaiting] = useState(false);
  const [photoWaiting, setPhotoWaiting] = useState(false);
  const [notifWaiting, setNotifWaiting] = useState(false);

  const hydrated = useRef(false);
  const dirty = useRef(false);
  const retriedMissingTrip = useRef(false);

  const setLocState = (v: LocationState) => {
    dirty.current = true;
    setLocStateRaw(v);
  };
  const setPhotos = (v: boolean) => {
    dirty.current = true;
    setPhotosRaw(v);
  };
  const setNotif = (v: boolean) => {
    dirty.current = true;
    setNotifRaw(v);
  };

  useEffect(() => {
    if (permsData?.permissions && !hydrated.current) {
      const p = permsData.permissions;
      setLocStateRaw(p.location_granted ? "enabled" : "not_requested");
      setPhotosRaw(p.photos_granted);
      setWifiRaw(p.wifi_only_pref);
      setNotifRaw(p.notifications_granted);
      hydrated.current = true;
    } else if (permsData && permsData.permissions === null && !hydrated.current) {
      hydrated.current = true;
    }
  }, [permsData]);

  useEffect(() => {
    if (!isNative()) return;
    (async () => {
      try {
        const { checkPhotoLibraryPermission } = await import("@/lib/native/permissions");
        const s = await checkPhotoLibraryPermission();
        setPhotoLimited(s === "limited");
        if (s === "granted" || s === "limited") {
          setPhotosRaw(true);
          if (hydrated.current && !photos) {
            dirty.current = true;
            setPhotosRaw(true);
          }
        }
      } catch {
        /* ignore */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = useMutation({
    mutationFn: (opts: { silent?: boolean } = {}) =>
      saveFn({
        data: {
          tripId: tripId!,
          locationGranted: locState === "enabled" || locState === "limited",
          photosGranted: photos,
          wifiOnlyPref: wifi,
          notificationsGranted: notif,
        },
      }).then((r) => ({ ...r, silent: opts.silent })),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ["perms"] });
      if (!r?.silent) {
        toast.success(t("perms.saved"));
        nav({ to: "/trip" });
      }
    },
    onError: (e: any) => toast.error(e.message),
  });

  useEffect(() => {
    if (!tripId || !hydrated.current || !dirty.current) return;
    const t = setTimeout(() => save.mutate({ silent: true }), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locState, photos, wifi, notif, tripId]);

  useEffect(() => {
    if (!tripData || tripData.trip || isFetchingTrip || retriedMissingTrip.current) return;
    retriedMissingTrip.current = true;
    void qc.refetchQueries({ queryKey: ["my-trip"], exact: true });
  }, [isFetchingTrip, qc, tripData]);

  async function requestLocation() {
    setLocWaiting(true);
    try {
      if (isNative()) {
        const state = await requestLocationAlways();
        let level: Awaited<ReturnType<typeof getLocationAuthLevel>> = "unknown";
        try {
          level = await getLocationAuthLevel();
        } catch {
          /* fall through */
        }

        if (level === "always") {
          setLocState("enabled");
          toast.success(t("perms.toast_loc_always"));
        } else if (level === "whenInUse") {
          setLocState("limited");
          toast.warning(t("perms.toast_loc_when_in_use"));
        } else if (level === "denied" || state === "denied") {
          setLocState("blocked");
          toast.error(t("perms.toast_loc_denied"));
        } else if (state === "granted") {
          setLocState("enabled");
          toast.success(t("perms.toast_loc_enabled"));
        } else if (state === "prompt" || state === "limited") {
          setLocState("limited");
          toast.warning(t("perms.toast_loc_when_in_use_2"));
        } else {
          setLocState("blocked");
          toast.error(t("perms.toast_loc_denied"));
        }
        return;
      }
      if (!("geolocation" in navigator)) {
        setLocState("blocked");
        toast.error(t("perms.toast_loc_unsupported"));
        return;
      }
      await new Promise<void>((resolve) => {
        navigator.geolocation.getCurrentPosition(
          () => {
            setLocState("enabled");
            toast.success(t("perms.toast_loc_enabled"));
            resolve();
          },
          (err) => {
            setLocState(err.code === err.PERMISSION_DENIED ? "blocked" : "limited");
            toast.error(t("perms.toast_loc_denied"));
            resolve();
          },
        );
      });
    } finally {
      setLocWaiting(false);
    }
  }

  async function connectPhotos() {
    setPhotoWaiting(true);
    try {
      if (isNative()) {
        const state = await requestPhotoLibraryReadWrite();
        const ok = state === "granted" || state === "limited";
        setPhotos(ok);
        setPhotoLimited(state === "limited");
        if (state === "limited")
          toast.warning(t("perms.toast_photo_limited"));
        else if (ok) toast.success(t("perms.toast_photo_connected"));
        else toast.error(t("perms.toast_photo_denied"));
        return;
      }
      setPhotos(true);
      toast.success(t("perms.toast_photo_web"));
    } finally {
      setPhotoWaiting(false);
    }
  }

  async function expandLimitedSelection() {
    try {
      const res = (await PhotoScan.presentLimitedPicker()) as
        | { supported?: boolean; message?: string }
        | undefined;
      // Current shipped native build returns { supported: false } — the in-app
      // limited-library picker isn't wired yet. Fall back to iOS Settings so
      // the user can tap "Edit Selected Photos" and add more photos.
      if (res && res.supported === false) {
        await openAppSettings();
        toast.message(t("perms.toast_photo_edit_selected"), {
          duration: 6000,
        });
        return;
      }
      const { clearThumbnailCache } = await import("@/lib/native/thumbnails");
      clearThumbnailCache();
      toast.success(t("perms.toast_photo_selection_updated"));
    } catch (e: any) {
      toast.error(e?.message ?? t("perms.toast_photo_picker_unavailable"));
    }
  }

  async function requestNotifications() {
    const wasBlocked = notifBlocked;
    setNotifWaiting(true);
    try {
      if (isNative()) {
        const state = await requestNotificationsPermission();
        if (state === "unsupported") {
          toast.error(t("perms.toast_notif_unsupported_device"));
          return;
        }
        const ok = state === "granted";
        setNotif(ok);
        setNotifBlocked(state === "denied");
        if (ok) toast.success(t("perms.toast_notif_enabled"));
        else if (state === "denied" && wasBlocked) {
          toast.error(t("perms.toast_notif_blocked_ios"));
          void openAppSettings();
        }
        return;
      }
      if (!("Notification" in window)) {
        toast.error(t("perms.toast_notif_unsupported"));
        return;
      }
      const r = await Notification.requestPermission();
      setNotif(r === "granted");
      setNotifBlocked(r === "denied");
      if (r === "granted") toast.success(t("perms.toast_notif_enabled"));
      else if (r === "denied" && wasBlocked) {
        toast.error(t("perms.toast_notif_blocked_web"));
        void openAppSettings();
      }
    } finally {
      setNotifWaiting(false);
    }
  }

  if (!tripId) {
    if (isFetchingTrip) {
      return (
        <RoavaPage title={t("perms.title")} subtitle={t("perms.loading_trip")}>
          <div className="h-14" />
        </RoavaPage>
      );
    }

    return (
      <RoavaPage title={t("perms.title")} subtitle={t("perms.no_trip_subtitle")}>
        <PrimaryButton
          onClick={() => nav({ to: "/join", search: {} })}
          className="h-14 rounded-2xl w-full"
        >
          {t("perms.enter_invite")}
        </PrimaryButton>
      </RoavaPage>
    );
  }

  const locReady = locState === "enabled" || locState === "limited";

  const locStatus: PermStatus = locWaiting
    ? "waiting"
    : locState === "enabled"
      ? "enabled"
      : locState === "limited"
        ? "limited"
        : locState === "blocked" || locState === "disabled"
          ? "blocked"
          : "not_granted";

  const photoStatus: PermStatus = photoWaiting
    ? "waiting"
    : photoLimited
      ? "limited"
      : photos
        ? "enabled"
        : "not_granted";

  const notifStatus: PermStatus = notifWaiting
    ? "waiting"
    : notif
      ? "enabled"
      : notifBlocked
        ? "blocked"
        : "not_granted";

  const chipLabels = {
    enabled: t("perms.chip_enabled"),
    not_granted: t("perms.chip_needs_action"),
    limited: t("perms.chip_limited"),
    checking: t("perms.chip_checking"),
    blocked: t("perms.chip_blocked"),
  };
  const locChip = statusToChip(locStatus, chipLabels);
  const photoChip = statusToChip(photoStatus, chipLabels);
  const notifChipLabel = notif ? t("perms.chip_on") : notifBlocked ? t("perms.chip_blocked") : t("perms.chip_off");
  const notifChipVariant: StatusChipVariant = notif ? "success" : notifBlocked ? "danger" : "neutral";

  const locHint = locState === "blocked" || locState === "disabled"
    ? t("perms.route_hint_blocked")
    : locState === "limited"
      ? t("perms.route_hint_limited")
      : null;

  return (
    <RoavaPage title={t("perms.title")} subtitle={t("perms.subtitle")}>

      {/* Offline pings — surfaced at the top so it's findable when needed. */}
      <div className="mb-4">
        <SyncOutboxCard />
        <div className="mt-2">
          <GpsHealthPanel />
        </div>
        <button
          type="button"
          onClick={() => nav({ to: "/offline-control" })}
          className="mt-2 w-full flex items-center justify-between rounded-xl border border-[var(--roava-border)] bg-[var(--roava-card)] px-3 py-2.5 text-sm font-medium text-[var(--roava-text)] hover:bg-[var(--roava-gray)]/50"
        >
          <span className="flex items-center gap-2">
            <CloudOff className="size-4 text-[var(--roava-text-muted)]" />
            {t("perms.offline_settings")}
          </span>
          <ChevronRight className="size-4 text-[var(--roava-text-muted)]" />
        </button>
      </div>

      {/* ---------------- Trip readiness ---------------- */}
      <SectionHeader
        title={t("perms.section_readiness")}
        hint={t("perms.section_readiness_hint")}
      />



      <div className="grid gap-3 min-w-0">
        {/* Route tracking — status card (tap to open full Route page) */}
        <div
          data-tour="row.routeTracking"
          role="button"
          tabIndex={0}
          onClick={() => nav({ to: "/tracking" })}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              nav({ to: "/tracking" });
            }
          }}
          className="cursor-pointer rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
        <RoavaCard
          icon={<MapPin className="size-5" />}
          iconTone="blue"
          title={
            <div className="flex items-center justify-between gap-2 min-w-0">
              <span className="min-w-0 flex-1 break-words">{t("perms.route_title")}</span>
              <div className="flex items-center gap-1.5 shrink-0 whitespace-nowrap">
                <StatusChip variant={locChip.variant}>{locChip.label}</StatusChip>
                <ChevronRight className="size-4 text-[var(--roava-text-muted)]" />
              </div>
            </div>
          }
          description={t("perms.route_desc")}
        >
          {locHint && (
            <p className="text-xs text-[var(--roava-text-muted)] rounded-lg bg-[var(--roava-gray)] px-2.5 py-1.5">
              {locHint}
            </p>
          )}
          <div
            className="mt-3 flex items-center justify-end gap-2 flex-wrap"
            onClick={(e) => e.stopPropagation()}
          >
            {locReady ? (
              <>
                <SecondaryButton size="sm" onClick={() => setGpsSheet(true)} className="rounded-xl gap-1" data-tour="btn.gpsDetail">
                  {t("perms.route_gps_detail")}
                </SecondaryButton>
                <SecondaryButton
                  size="sm"
                  onClick={requestLocation}
                  disabled={locWaiting}
                  className="rounded-xl"
                >
                  {locWaiting ? t("perms.chip_checking") : t("perms.route_recheck")}
                </SecondaryButton>
              </>
            ) : locState === "blocked" || locState === "disabled" ? (
              <PrimaryButton
                size="sm"
                onClick={() => void openAppSettings()}
                className="rounded-xl gap-1"
              >
                <SettingsIcon className="size-3.5" />
                {t("perms.route_open_settings")}
              </PrimaryButton>
            ) : (
              <PrimaryButton
                size="sm"
                onClick={requestLocation}
                disabled={locWaiting}
                className="rounded-xl"
                data-tour="btn.enableLocation"
              >
                {locWaiting ? t("perms.chip_checking") : t("perms.route_enable")}
              </PrimaryButton>
            )}
          </div>
        </RoavaCard>
        </div>



        {/* Photos & videos — status card */}
        <div data-tour="row.photos">
        <RoavaCard
          icon={<Camera className="size-5" />}
          iconTone="blue"
          title={
            <div className="flex items-center justify-between gap-2 min-w-0">
              <span className="min-w-0 flex-1 break-words">{t("perms.photos_title")}</span>
              <span className="shrink-0 whitespace-nowrap"><StatusChip variant={photoChip.variant}>{photoChip.label}</StatusChip></span>
            </div>
          }
          description={t("perms.photos_desc")}
        >
          {photoLimited && (
            <p className="text-xs text-[var(--roava-text-muted)] rounded-lg bg-[var(--roava-gray)] px-2.5 py-1.5">
              {t("perms.photos_only_selected")}
            </p>
          )}
          {(!photos || (photos && photoLimited && isNative())) && (
            <div className="mt-3 flex items-center justify-end gap-2 flex-wrap">
              {!photos && (
                <PrimaryButton
                  size="sm"
                  onClick={connectPhotos}
                  disabled={photoWaiting}
                  className="rounded-xl"
                  data-tour="btn.enablePhotos"
                >
                  {photoWaiting ? t("perms.chip_checking") : t("perms.photos_review")}
                </PrimaryButton>
              )}
              {photos && photoLimited && isNative() && (
                <SecondaryButton size="sm" onClick={expandLimitedSelection} className="rounded-xl">
                  {t("perms.photos_choose_more")}
                </SecondaryButton>
              )}
            </div>
          )}
        </RoavaCard>
        </div>

        {/* Pod grouping — navigation card */}
        <div data-tour="row.pods">
        <NavCard
          icon={<Layers className="size-5" />}
          iconTone="blue"
          title={t("perms.pods_title")}
          description={t("perms.pods_desc")}
          value={<span className="text-sm text-[var(--roava-text-muted)]">{podChipLabel}</span>}
          onClick={() => setPodsSheet(true)}
        />
        </div>

      </div>

      {/* ---------------- Optional ---------------- */}
      <Collapsible className="mt-6 rounded-2xl border border-border bg-card">
        <CollapsibleTrigger className="group flex w-full items-center justify-between gap-2 px-4 py-3 text-left">
          <div className="flex items-center gap-2">
            <Settings2 className="size-4 text-[var(--roava-text-muted)]" />
            <span className="text-sm font-semibold">{t("perms.optional")}</span>
          </div>
          <ChevronDown className="size-4 text-[var(--roava-text-muted)] transition-transform group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent className="px-3 pb-3">
          <p className="text-xs text-[var(--roava-text-muted)] px-1 pb-3">
            {t("perms.optional_hint")}
          </p>
          <div className="grid gap-3 min-w-0">
            {/* Notifications — navigation card with status */}
            <div data-tour="row.notifications">
            <NavCard
              icon={<Bell className="size-5" />}
              iconTone="blue"
              title={t("perms.notif_title")}
              description={t("perms.notif_desc")}
              value={<StatusChip variant={notifChipVariant}>{notifChipLabel}</StatusChip>}
              onClick={() => nav({ to: "/notifications" })}
            />
            </div>
            {!notif && (
              <div className="-mt-1 pl-2">
                <button
                  type="button"
                  onClick={() => {
                    if (notifBlocked) void openAppSettings();
                    else void requestNotifications();
                  }}
                  disabled={notifWaiting}
                  className="text-xs font-medium text-[var(--roava-blue)] disabled:opacity-50"
                >
                  {notifWaiting
                    ? t("perms.chip_checking")
                    : notifBlocked
                      ? t("perms.notif_open_settings")
                      : t("perms.notif_enable")}
                </button>
              </div>
            )}

            <LiveActivityRow />
            <BiometricRow />

            <div data-tour="row.uploadSync">
            <NavCard
              icon={<Upload className="size-5" />}
              iconTone="blue"
              title={t("perms.upload_title")}
              description={t("perms.upload_desc")}
              onClick={() => nav({ to: "/upload-mode" })}
            />
            </div>

            <NavCard
              icon={<Compass className="size-5" />}
              iconTone="blue"
              title={t("perms.tour_quick_title")}
              description={t("perms.tour_quick_desc")}
              onClick={() => {
                resetOnboarding();
                requestOpenOnboarding();
              }}
            />

            <NavCard
              icon={<Compass className="size-5" />}
              iconTone="blue"
              title={t("perms.tour_full_title")}
              description={t("perms.tour_full_desc")}
              onClick={() => startTour("full-walkthrough")}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>



      {/* ---------------- Account ---------------- */}
      <SectionHeader title={t("perms.section_account")} />

      <div className="grid gap-3 min-w-0">
        {/* My profile */}
        <NavCard
          icon={
            <Avatar className="size-12">
              {me?.avatarUrl && <AvatarImage src={me.avatarUrl} alt={t("perms.your_avatar")} />}
              <AvatarFallback className="text-lg bg-[var(--roava-gray)] text-[var(--roava-text)]">
                {(me?.displayName ?? me?.email ?? "?").slice(0, 1).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          }
          iconTone="gray"
          title={t("perms.my_profile")}
          description={t("perms.my_profile_desc")}
          onClick={() => nav({ to: "/profile" })}
        />

        {tripData?.isAdmin && (
          <NavCard
            icon={<Shield className="size-5" />}
            iconTone="blue"
            title={t("perms.admin_console")}
            description={t("perms.admin_console_desc")}
            onClick={() => nav({ to: "/admin" })}
          />
        )}

        <AccountButtons />
      </div>

      <GpsRateSheet open={gpsSheet} onOpenChange={setGpsSheet} />
      <PodsSheet open={podsSheet} onOpenChange={setPodsSheet} />

      {/* Build info + extra bottom spacing so nothing hides behind the floating tab bar */}
      <div className="mt-8 flex justify-center">
        <div className="rounded-full border border-[var(--roava-border)] bg-[var(--roava-card)]/60 px-3 py-1.5 inline-flex">
          <BuildInfoLine />
        </div>
      </div>
      <div className="h-16" aria-hidden />
    </RoavaPage>
  );
}

function AccountButtons() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  async function handleSignOut() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.id) {
        const { count } = await supabase
          .from("media_assets")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id)
          .in("upload_status", ["queued", "uploading", "failed"]);
        if ((count ?? 0) > 0) {
          toast(t("perms.pending_uploads_warn"));
        }
      }
    } catch { /* noop */ }
    try { await (await import("@/lib/cache/clearOnSignOut")).clearMediaCacheForCurrentUser(); } catch { /* noop */ }
    (await import("@/lib/offline/offline-auth")).markUserInitiatedSignOut();
    await supabase.auth.signOut();
    nav({ to: "/login" });
  }

  async function handleDelete() {
    if (confirmText !== "DELETE") return;
    setDeleting(true);
    try {
      const { error } = await supabase.rpc("delete_own_account");
      if (error) throw error;
      try { await (await import("@/lib/cache/clearOnSignOut")).clearMediaCacheForCurrentUser(); } catch { /* noop */ }
      (await import("@/lib/offline/offline-auth")).markUserInitiatedSignOut();
      await supabase.auth.signOut();
      toast.success(t("perms.account_deleted"));
      nav({ to: "/" });
    } catch (e: any) {
      toast.error(e?.message ?? t("perms.delete_failed"));
      setDeleting(false);
    }
  }

  return (
    <div className="grid gap-3 mt-1 min-w-0">
      <SecondaryButton
        onClick={handleSignOut}
        className="h-12 rounded-2xl w-full justify-center gap-2"
      >
        <LogOut className="size-4" />
        {t("perms.sign_out")}
      </SecondaryButton>

      <AlertDialog onOpenChange={(o) => !o && setConfirmText("")}>
        <AlertDialogTrigger asChild>
          <DangerButton className="h-12 rounded-2xl w-full justify-center gap-2">
            <Trash2 className="size-4" />
            {t("perms.delete_account")}
          </DangerButton>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("perms.delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("perms.delete_body")}
              <br />
              <br />
              {t("perms.delete_type_confirm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="DELETE"
            className="rounded-xl"
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t("perms.delete_cancel")}</AlertDialogCancel>
            <AlertDialogAction
              disabled={confirmText !== "DELETE" || deleting}
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              className="bg-[var(--roava-red)] text-[var(--roava-red-foreground)] hover:opacity-90"
            >
              {deleting ? t("perms.delete_deleting") : t("perms.delete_forever")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function LiveActivityRow() {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState<boolean>(() => getLiveActivityEnabled());
  if (!isNative()) return null;

  function toggle(next: boolean) {
    setLiveActivityEnabled(next);
    setEnabled(next);
    try { liveActivityController.onPreferenceChanged(next); } catch { /* non-fatal */ }
    toast.success(next ? t("perms.live_on_toast") : t("perms.live_off_toast"));
  }

  return (
    <RoavaCard
      icon={<Radio className="size-5" />}
      iconTone="blue"
      title={
        <div className="flex items-center justify-between gap-2">
          <span>{t("perms.live_title")}</span>
          <Switch checked={enabled} onCheckedChange={toggle} />
        </div>
      }
      description={t("perms.live_desc")}
    />
  );
}

function BiometricRow() {
  const { t } = useTranslation();

  type Status = Awaited<ReturnType<typeof import("@/lib/native").getBiometricStatus>>;
  const [status, setStatus] = useState<Status | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastChecked, setLastChecked] = useState<number | null>(null);

  async function refresh() {
    const { getBiometricStatus } = await import("@/lib/native");
    const [s, e] = await Promise.all([getBiometricStatus(), isBiometricUnlockEnabled()]);
    setStatus(s);
    setEnabled(e);
    setLastChecked(Date.now());
  }

  useEffect(() => {
    void refresh();
  }, []);

  if (!isNative()) return null;

  async function toggle(next: boolean) {
    if (busy) return;
    setBusy(true);
    try {
      if (next) {
        await verifyBiometric(t("perms.bio_prompt"));
        await setBiometricUnlockEnabled(true);
        setEnabled(true);
        toast.success(t("perms.bio_on_toast"));
      } else {
        await setBiometricUnlockEnabled(false);
        setEnabled(false);
        toast.success(t("perms.bio_off_toast"));
      }
    } catch (e: any) {
      toast.error(e?.message ?? t("perms.bio_failed"));
    } finally {
      setBusy(false);
    }
  }

  const available = status?.kind === "available";
  const pluginMissing = status?.kind === "plugin_missing";
  const bioLabel =
    status && "biometryType" in status && status.biometryType
      ? biometryTypeLabel(status.biometryType)
      : undefined;
  const note = !status
    ? t("perms.bio_note_checking")
    : status.kind === "available"
      ? (bioLabel ? t("perms.bio_note_available_with", { label: bioLabel }) : t("perms.bio_note_available") + ".")
      : status.kind === "not_enrolled"
        ? t("perms.bio_note_not_enrolled")
        : status.kind === "plugin_missing"
          ? t("perms.bio_note_plugin_missing")
          : status.kind === "errored"
            ? t("perms.bio_note_error", { message: status.message })
            : t("perms.bio_note_unavailable");

  const checkedAgo = lastChecked
    ? (() => {
        const s = Math.max(0, Math.floor((Date.now() - lastChecked) / 1000));
        return s < 60 ? t("perms.bio_checked_s", { n: s }) : t("perms.bio_checked_m", { n: Math.floor(s / 60) });
      })()
    : null;

  const chipVariant: StatusChipVariant =
    enabled && available ? "success" : pluginMissing ? "danger" : "neutral";
  const chipLabel = enabled && available ? t("perms.chip_on") : pluginMissing ? t("perms.chip_unavailable") : t("perms.chip_off");

  return (
    <RoavaCard
      icon={<Fingerprint className="size-5" />}
      iconTone="blue"
      title={
        <div className="flex items-center justify-between gap-2">
          <span>{t("perms.bio_title")}</span>
          {available ? (
            <Switch checked={enabled} onCheckedChange={toggle} disabled={busy} />
          ) : (
            <StatusChip variant={chipVariant}>{chipLabel}</StatusChip>
          )}
        </div>
      }
      description={t("perms.bio_desc")}
    >
      <p className="text-xs text-[var(--roava-text-muted)]">{note}</p>
      {status?.kind === "not_enrolled" && (
        <div className="mt-2 flex items-center justify-end gap-3">
          {checkedAgo && (
            <span className="text-[11px] text-[var(--roava-text-muted)]/70">{checkedAgo}</span>
          )}
          <Button
            type="button"
            variant="link"
            onClick={() => void refresh()}
            className="px-0 h-auto text-xs text-[var(--roava-blue)]"
          >
            {t("perms.bio_recheck")}
          </Button>
        </div>
      )}
    </RoavaCard>
  );
}
